import React, { PureComponent, Fragment, createRef } from 'react';
import { connect } from 'react-redux';
import cn from 'classnames';
import { DateTime, DateTimeOptions, Settings } from 'luxon';
import memoizeOne from 'memoize-one';
import { requestAsync } from 'redux-query';
import { HeaderText, Button, Icon, Spinner } from '@el8/vital';
import { Box } from 'components';
import { getBookingInfo, updateBookingInfo } from 'modules/booking';
import {
  getAvailabilitiesForAppointmentType,
  getAvailabilitiesForAppointmentTypeQuery,
} from 'modules/availability';

import { State, LuxonFormats, Dispatch } from 'types';
import styles from './Calendar.less';

// Types
type CalendarOwnProps = {
  appointmentTypeId: number;
  locationDates?: { [key: string]: string[] };
  practiceId: number;
  selectAvailability: (date: string, time: string) => void;
  state: string;
};

type CalendarStateProps = {
  currentMonth: string;
  dates: {
    [key: string]: string[];
  };
  isLoadingAvailabilities: boolean;
  isPresentMonth: boolean;
  selectedDate: string;
  todayDate: string;
  selectedTimezone: string;
};

type CalendarDispatchProps = {
  fetchAvailabilitiesForMonth: (monthStart: string) => void;
};

type CalendarProps = {} & CalendarOwnProps & CalendarStateProps & CalendarDispatchProps;

type CalendarState = {
  selectedDate: string;
  opacity: number;
  height: number;
};

// Code
const daysOfWeek = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

const getCurrentMonthLuxonDate = memoizeOne((date: string): DateTime => DateTime.fromISO(date));
const getCurrentWeekProps = memoizeOne((date?: string): [number, number] | [] => {
  if (!date) {
    return [];
  }
  const lDate = DateTime.fromISO(date);
  return [lDate.weekday, lDate.weekNumber];
});

export const getMonth = memoizeOne((date: DateTime, skipPastWeeks?: boolean): DateTime[][] => {
  const today = DateTime.fromObject({ hour: 0, minute: 0, second: 0 });
  let currentDate = date;

  if (skipPastWeeks && currentDate < today) {
    const weekStart = today.minus({ days: today.weekday % 7 });

    if (weekStart.month === currentDate.month) {
      currentDate = weekStart;
    }
  }

  const month = [];

  while (currentDate.month === date.month) {
    const week = [];
    for (let i = 0; i <= 6; i++) {
      if (currentDate.weekday % 7 === i) {
        week.push(currentDate);
        currentDate = currentDate.plus({ days: 1 });
      } else {
        week.push(currentDate.minus({ days: (currentDate.weekday % 7) - i }));
      }
    }
    month.push(week);
  }
  return month;
});

const mapStateToProps = (
  state: State,
  { appointmentTypeId }: CalendarOwnProps,
): CalendarStateProps => {
  const { selectedPhysicianId, calendar, scheduledDate, timezone } = getBookingInfo(state);
  const { currentMonth, isLoadingAvailabilities } = calendar;
  const luxonCurrentMonth = DateTime.fromISO(currentMonth);
  const now = DateTime.fromObject({ zone: Settings.defaultZone });
  const isPresentMonth = now.month === luxonCurrentMonth.month;
  const todayDate = now.toISODate();

  return {
    currentMonth,
    dates: getAvailabilitiesForAppointmentType(state, {
      appointmentTypeId,
      endDate: luxonCurrentMonth.endOf('month').toISODate(),
      physicianId: selectedPhysicianId,
      startDate: isPresentMonth ? todayDate : currentMonth,
      startTime: isPresentMonth ? now.toFormat('HH:mm') : undefined,
    }),
    isPresentMonth,
    isLoadingAvailabilities,
    selectedDate: scheduledDate ? DateTime.fromISO(scheduledDate).toISODate() : undefined,
    todayDate,
    selectedTimezone: timezone,
  };
};

const mapDispatchToProps = (
  dispatch: Dispatch,
  { appointmentTypeId, practiceId }: CalendarOwnProps,
): CalendarDispatchProps => ({
  fetchAvailabilitiesForMonth: (monthStart: string): void => {
    const isAvailabilityOnBkSiteEnabled = window.el8Globals.FEATURES.AvailabilityOnBkSite;
    dispatch(
      updateBookingInfo({ calendar: { isLoadingAvailabilities: true, currentMonth: monthStart } }),
    );
    if (!isAvailabilityOnBkSiteEnabled) {
      dispatch(
        requestAsync(
          getAvailabilitiesForAppointmentTypeQuery(practiceId, {
            appointmentTypeId,
            endDate: DateTime.fromISO(monthStart).endOf('month').toISODate(),
            force: true,
            startDate: monthStart,
          }),
        ),
      ).finally((): void => {
        dispatch(updateBookingInfo({ calendar: { isLoadingAvailabilities: false } }));
      });
    }
  },
});

const mergeProps = (
  stateProps: CalendarStateProps,
  dispatchProps: CalendarDispatchProps,
  ownProps: CalendarOwnProps,
): CalendarProps => ({
  ...ownProps,
  ...stateProps,
  ...dispatchProps,
});

const initialStyle = {
  height: 0,
  opacity: 0,
};

class Calendar extends PureComponent<CalendarProps, CalendarState> {
  calendar: React.RefObject<HTMLDivElement>;

  calendarContainer: React.RefObject<HTMLDivElement>;

  constructor(props: CalendarProps) {
    super(props);

    let selectedDate;
    const dates = this.props.locationDates || this.props.dates;

    if (props.selectedDate && dates[props.selectedDate]) {
      // eslint-disable-next-line prefer-destructuring
      selectedDate = props.selectedDate;
    } else if (dates) {
      selectedDate = Object.keys(dates).find(
        (date): boolean => dates[date] && !!dates[date].length,
      );
    }

    this.state = {
      height: 0,
      opacity: 0,
      selectedDate,
    };

    this.calendar = createRef();
    this.calendarContainer = createRef();
  }

  componentDidMount(): void {
    this.setState({
      height: this.calendar.current.scrollHeight,
      opacity: 1,
    });
  }

  componentDidUpdate(pp: CalendarProps, ps: CalendarState): void {
    const { currentMonth } = this.props;
    const dates = this.props.locationDates || this.props.dates;
    const { selectedDate } = this.state;

    if (currentMonth !== pp.currentMonth) {
      const newSelectedDate = dates
        ? Object.keys(dates).find((date) => dates[date] && !!dates[date].length)
        : undefined;
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({ selectedDate: newSelectedDate });
    }

    if (dates && !(selectedDate && dates[selectedDate])) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({
        selectedDate: Object.keys(dates).find((date) => dates[date] && !!dates[date].length),
      });
    }

    if (selectedDate !== ps.selectedDate || currentMonth !== pp.currentMonth) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({ height: this.calendar.current.scrollHeight });
    }
  }

  handleSelectDate = (date: string) => (): void => {
    this.setState({ selectedDate: date });
  };

  handleNextMonthClick = (): void => {
    const { currentMonth, fetchAvailabilitiesForMonth } = this.props;
    fetchAvailabilitiesForMonth(DateTime.fromISO(currentMonth).plus({ months: 1 }).toISODate());
  };

  handlePrevMonthClick = (): void => {
    const { currentMonth, fetchAvailabilitiesForMonth } = this.props;
    fetchAvailabilitiesForMonth(DateTime.fromISO(currentMonth).minus({ months: 1 }).toISODate());
  };

  render(): JSX.Element {
    const { selectedDate, opacity, height } = this.state;
    const {
      isLoadingAvailabilities,
      isPresentMonth,
      selectAvailability,
      todayDate,
      state,
      selectedTimezone,
    } = this.props;
    const currentMonth = getCurrentMonthLuxonDate(this.props.currentMonth);
    const [currentWeekday, currentWeek] = getCurrentWeekProps(selectedDate);
    const dates = this.props.locationDates || this.props.dates;

    // The 'local' argument for Luxon's setZone method will default to the value
    // set in Settings.defaultZoneName (the practice timezone set in Booking.tsx).
    // https://moment.github.io/luxon/api-docs/index.html#datetimesetzone
    const timezone = window.el8Globals.FEATURES.SchedulingTimeZone ? selectedTimezone : 'local';

    const transitionStyles: { [key: string]: React.CSSProperties } = {
      entered: { opacity, height },
      entering: { opacity, height },
      exited: { opacity: 0, height: 0 },
      exiting: { opacity: 0, height: 0 },
    };
    return (
      <div
        className={cn(styles.calendarContainer, styles[state])}
        style={{ ...initialStyle, ...transitionStyles[state] }}
        ref={this.calendarContainer}
      >
        <div className={cn(styles.calendar, styles[state])} ref={this.calendar}>
          <HeaderText tag="h2" legacyVariant="legacyHeaderText" sizing="subheading-md">
            {currentMonth.toFormat('LLLL yyyy')}
          </HeaderText>
          <Box className={styles.header} justify="space-between">
            {!isPresentMonth && (
              <Icon
                icon="chevron-left"
                iconSize={18}
                className={styles.back}
                title="Previous month"
                onClick={this.handlePrevMonthClick}
              />
            )}
            {daysOfWeek.map(
              (day): JSX.Element => (
                <div key={day} className={styles.dayOfWeek}>
                  {day}
                </div>
              ),
            )}
            <Icon
              icon="chevron-right"
              iconSize={18}
              className={styles.forward}
              title="Next month"
              onClick={this.handleNextMonthClick}
            />
          </Box>
          <div className={styles.body}>
            {getMonth(currentMonth, true).map(
              (week): JSX.Element => (
                <Fragment key={week[0].weekNumber}>
                  <Box key="week" className={styles.week} justify="space-between">
                    {week.map((date): JSX.Element => {
                      const isoDate = date.toISODate();
                      const disabled = !(dates && dates[isoDate] && dates[isoDate].length);
                      const inCurrentMonth =
                        date >= currentMonth && date <= currentMonth.endOf('month');
                      return (
                        // eslint-disable-next-line jsx-a11y/click-events-have-key-events
                        <div
                          key={isoDate}
                          className={cn(
                            styles.date,
                            disabled && styles.isDisabled,
                            isoDate === todayDate && styles.today,
                            selectedDate === isoDate && inCurrentMonth && styles.isActive,
                          )}
                          onClick={this.handleSelectDate(isoDate)}
                          role="button"
                          tabIndex={0}
                        >
                          {inCurrentMonth ? date.day : ''}
                        </div>
                      );
                    })}
                  </Box>
                  {selectedDate &&
                    week[currentWeekday % 7].weekNumber === currentWeek &&
                    dates &&
                    dates[selectedDate] &&
                    dates[selectedDate].length && (
                      <Box key="hours" className={styles.hoursContainer} wrap>
                        {dates[selectedDate].map((hour) => (
                          <Button
                            appearance="outline"
                            key={hour}
                            className={styles.slot}
                            onClick={(): void => selectAvailability(selectedDate, hour)}
                          >
                            {DateTime.fromFormat(
                              `${selectedDate}, ${hour}`,
                              `${LuxonFormats.ServerDate}, HH:mm`,
                            )
                              .setZone(timezone)
                              .toLocaleString(DateTime.TIME_SIMPLE as DateTimeOptions)}
                          </Button>
                        ))}
                      </Box>
                    )}
                </Fragment>
              ),
            )}
            {isLoadingAvailabilities && (
              <Box className={styles.loadingOverlay} align="center" justify="center">
                <Spinner />
              </Box>
            )}
          </div>
        </div>
      </div>
    );
  }
}

export default connect(mapStateToProps, mapDispatchToProps, mergeProps)(Calendar);
